<template lang="pug">
div(:class="[$style.headerGrid, {'is-ppc': isPPC}]")
  Block(
     v-for="(innerBlock, key) in block.innerBlocks"
    :key="`headerBlock${key}`"
    :id="'header-block-' + key"
    :class="['header-block', { 'with-rocket': hasRocket && key === 1 }]"
    :block="innerBlock"
    :block-id="blockId"
    :lazy="false")
</template>

<script lang="ts">
import { defineComponent, onMounted } from "vue"

export default defineComponent({
  props: {
    block: { required: true, type: Object as () => Block },
    blockId: { default: "", type: String },
    narrowLhs: { default: false, type: Boolean },
    isPPC: { default: false, type: Boolean }
  },
  data() {
    return {
      resizeHandler: undefined as ((event: UIEvent) => void) | undefined
    }
  },
  setup(props) {
    const hasRocket = computed(() => {
    // Find the className attribute
    const classAttr = props.block.attributes?.find(attr =>
      attr.name === 'className'
    )
    return classAttr?.value?.includes('with-rocket') || false
  })
    return {
      hasRocket
    }
  },
  mounted() {
    const img: HTMLImageElement | null = document.querySelector('#header-block-2 img');
    if (img) {
      img.classList.add('img-show')
      this.adjustImageFit(img)
      this.resizeHandler = () => this.adjustImageFit(img)
      window.addEventListener('resize', this.resizeHandler);
    }
  },
  unmounted() {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
    }
  },
  methods: {
    adjustImageFit(img: HTMLImageElement): void {
      if (window.innerWidth >= 992) {
        const clientAspect = img.clientWidth / img.clientHeight;
        const naturalAspect = img.naturalWidth / img.naturalHeight;

        if (clientAspect >= naturalAspect) {
          img.classList.add('img-contain-right');
        } else {
          img.classList.remove('img-contain-right');
        }
      }
    }
  }
})
</script>

<style lang="css" module>
.headerGrid {
  @apply grid gap-y-ds-sm mb-ds-md;

  grid-template-columns: 3fr 1fr;
  grid-template-rows: min-content min-content 1fr;
  align-items: center;
  grid-template-areas:
    "header image"
    "paragraph image"
    "locationForm locationForm";

  &:global(.content) {
    @apply py-0;
  }

  &:global(.is-ppc) {
    :global(.query-maintitle) {
      opacity: 0;
    }
  }

  &:global(:not(.is-ppc)) {
    :global(.query-maintitle) {
      opacity: 1;
    }
  }

  :global {
    .av-heading {
      @apply text-rebrandXm xs:text-rebrandLg md:text-rebrand2xm xl:text-rebrandXl leading-normal m-0 mt-ds-md md:mt-ds-lg lg:mt-0;
    }
    .av-paragraph {
      @apply text-base xs:text-rebrandSm md:text-rebrandMd lg:text-rebrandMd leading-normal m-0 lg:mb-ds-sm;
    }

    /* Apply emoji to header blocks with with-rocket class */
    .header-block.with-rocket::before {
      content: '🚀';
      font-size: 2rem;
      margin-right: 12px;
      display: inline-block;
    }

  }

  > *:nth-child(1) {
    @apply self-end lg:justify-self-start;
    grid-area: header;
  }

  > *:nth-child(2) {
    @apply self-start;
    grid-area: paragraph;
  }

  > *:nth-child(3) {
    @apply w-full relative lg:static overflow-hidden self-center justify-self-center -right-ds-md md:right-[-32px] mt-ds-md lg:m-0;
    grid-area: image; /* or LocationFormExtension */

    &:has(:global(.extension-block-container)) {
      @apply h-[calc(100%-105px)] self-start mt-ds-xxl overflow-visible;
    }
  }

  > *:nth-child(4) {
    @apply self-start mb-ds-md md:mb-ds-lg lg:mb-0;
    grid-area: locationForm;

    :global {
      .cms-form {
        @apply mt-0;
      }
    }
  }
}

@media (max-width: 991px) {
  .headerGrid {
    > *:nth-child(3) {
      @apply self-start;

      img {
        @apply max-h-[134px] ml-auto;
      }
    }
  }
}

@media (min-width: 1300px) {
  .headerGrid {
    > *:nth-child(3) {
      img {
        @apply opacity-0;
        transition: opacity 0.3s ease;

        &:global(.img-show) {
          @apply opacity-100;
        }
      }
    }
  }
}

@screen lg {
  .headerGrid {
    @apply h-[520px] min-h-fit mb-0 py-ds-ml gap-x-ds-md items-center;

    grid-template-columns: minmax(min-content, 550px) 1fr;
    grid-template-rows: auto min-content auto;
    grid-template-areas:
      "header image"
      "paragraph image"
      "locationForm image";

    > *:nth-child(3) {
      @apply absolute right-0 top-0 bottom-0 w-full h-full max-w-[44vw] min-h-full max-h-[440px];

      img {
        @apply w-full h-full;
        @apply object-cover object-left;

        &:global(.img-contain-right) {
          @apply object-contain object-right;
        }
      }
    }

    > *:nth-child(1),
    > *:nth-child(2),
    > *:nth-child(4) {
      @apply z-[2];
    }
  }
}

@screen xl {
  .headerGrid {
    grid-template-columns: minmax(min-content, 650px) 1fr;
  }
}

@screen xxl {
  .headerGrid {
    > *:nth-child(3) {
      @apply top-[-24px] bottom-[-24px] h-[calc(100%+48px)] min-h-[calc(100%+48px)];
    }
  }
}
</style>
